'use client';
import { cache, useState } from 'react';
import useBackend from '@/backend';

import { ClientResponse, UserRequestBody } from './types';
import { useAccessToken, useReady } from '@/store';
import { useGetUser } from '../get-user/get-user';
import { User } from '../get-user/types';

interface Args {
	accessToken?: string | null;
	body: Partial<User>;
}

/* --------------------------------- OPTIONS -------------------------------- */

const route = '/user/update';

const headers = {
	'Content-Type': 'application/json',
	Authorization: null,
};

/* -------------------------------------------------------------------------- */

export const useUpdateUser = () => {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const { url, getAccessToken } = useBackend();

	const { getUser } = useGetUser();

	const updateUser = cache(async ({ body }: Args) => {
		setLoading(true);
		setError(null);

		try {
			const apiUrl = `${url}${route}`;
			/* -------------------------------------------------------------------------- */
			const token = await getAccessToken({});
			if (!token) throw new Error('Failed to load');
			headers.Authorization = token;

			/* -------------------------------------------------------------------------- */

			const user = await fetch(apiUrl, {
				method: 'POST',
				headers: headers,
				body: JSON.stringify(body),
			});
			const userData = await user.json();
			if (userData?.errors.length) {
				throw new Error(userData.errors[0]);
			}
			getUser({});

			return { user: userData?.data?.user as ClientResponse, error: null };
		} catch (e: unknown) {
			const message = e instanceof Error ? e.message : 'Unknown error';
			setError(message);
			if (e instanceof Error) return { user: null, error: e.message };

			return { user: null, error: 'Failed to load user data' };
		} finally {
			setLoading(false);
		}
	});
	return { updateUser, loading, error };
};
